import React, { useEffect } from 'react';
import { Modal, Table, Spinner, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDeviceHistory } from '../../store/features/devicesSlice';

function DeviceHistoryModal({ show, onHide, device }) {
    const dispatch = useDispatch();
    const { deviceHistory, historyStatus, historyError } = useSelector((state) => state.devices);

    useEffect(() => {
        if (show && device?.device_id) {
            dispatch(fetchDeviceHistory(device.device_id));
        }
    }, [show, device?.device_id, dispatch]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getTransferType = (current, previous) => {
        if (!previous) return 'Initial Assignment';

        const changes = [];
        if (current.partner_id !== previous.partner_id) changes.push('Partner');
        if (current.field_officer_id !== previous.field_officer_id) changes.push('Field Officer');
        if ((current.hardware_id || null) !== (previous.hardware_id || null)) changes.push('Hardware');
        if ((current.display_software_id || null) !== (previous.display_software_id || null)) changes.push('Display SW');
        if ((current.device_software_id || null) !== (previous.device_software_id || null)) changes.push('Firmware');
        if ((current.warranty_end_date || null) !== (previous.warranty_end_date || null)) changes.push('Warranty');

        return changes.length > 0 ? changes.join(', ') : 'No Change';
    };

    const formatWarranty = (value) => {
        if (!value) return '—';
        return new Date(value).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Device History - {device?.device_id}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {historyStatus === 'loading' && (
                    <div className="text-center py-4">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <p className="mt-2">Loading device history...</p>
                    </div>
                )}

                {historyError && (
                    <Alert variant="danger">
                        {historyError}
                    </Alert>
                )}

                {historyStatus === 'succeeded' && (
                    <>
                        {deviceHistory.length === 0 ? (
                            <Alert variant="info">
                                No history found for this device.
                            </Alert>
                        ) : (
                            <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                <Table striped bordered hover size="sm">
                                    <thead className="table-dark" style={{ position: 'sticky', top: 0 }}>
                                        <tr>
                                            <th>#</th>
                                            <th>Date & Time</th>
                                            <th>Partner</th>
                                            <th>Field Officer</th>
                                            <th>Hardware</th>
                                            <th>Display SW</th>
                                            <th>Firmware</th>
                                            <th>Warranty End</th>
                                            <th>Changes</th>
                                            <th>Changed By</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {deviceHistory.map((record, index) => {
                                            const previous = index > 0 ? deviceHistory[index - 1] : null;
                                            const transferType = getTransferType(record, previous);

                                            return (
                                                <tr key={record.device_history_id}>
                                                    <td>{index + 1}</td>
                                                    <td>{formatDate(record.created_at)}</td>
                                                    <td className="text-capitalize">
                                                        {record.partner_name || (
                                                            <span className="text-muted">Unassigned</span>
                                                        )}
                                                    </td>
                                                    <td className="text-capitalize">
                                                        {record.field_officer_name || (
                                                            <span className="text-muted">Unassigned</span>
                                                        )}
                                                    </td>
                                                    <td className="text-uppercase small">
                                                        {record.hardware_version || '—'}
                                                    </td>
                                                    <td className="small">
                                                        {record.display_software_version || '—'}
                                                    </td>
                                                    <td className="small">
                                                        {record.device_software_version || '—'}
                                                    </td>
                                                    <td className="small">
                                                        {formatWarranty(record.warranty_end_date)}
                                                    </td>
                                                    <td>
                                                        <span className="badge bg-secondary">{transferType}</span>
                                                    </td>
                                                    <td className="text-capitalize">
                                                        {record.created_by_name || 'System'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-secondary" onClick={onHide}>
                    Close
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default DeviceHistoryModal;
